import React, {
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { FormProvider } from "react-hook-form";
import { useLocation, useParams } from "react-router";
import {
  type GetSubmissionTypeForBuilderQuery,
  useGetSubmissionTypeForBuilderQuery,
} from "../../../../../generated/graphql";
import { AuthContext } from "../../../../Authorization/AuthContext";
import type { AuthContextType } from "../../../../Authorization/types";
import FullPageFormLayout from "../../../../Common/FullPageFormLayout";
import FullPageLoadingScreen from "../../../../Common/FullPageLoadingScreen";
import { SubmissionsBuilderWrapper } from "./__styles__/SubmissionsBuilder";
import { useSubmissionBuilderV2ChatAgent } from "./BuilderChatAgent";
import { useShowCreateSubmissionTypeModalOnEnter } from "./CreateSubmissionTypeModal";
import { getDefaultValues, useBuilderForm, useBuilderWatch } from "./form/form";
import { customResolver } from "./form/validation/resolver";
import { HeaderMiddleContainer } from "./HeaderMiddleContainer";
import { MainColumn } from "./MainColumn/MainColumn";
import { MenuButtons } from "./MenuButtons";
import { SidebarColumn } from "./SidebarColumn/SidebarColumn";
import {
  SUBMISSIONS_BUILDER_MODE,
  SubmissionsBuilderContextProvider,
  useSubmissionsBuilderContext,
  useSubmissionsBuilderMode,
} from "./SubmissionsBuilderContext";
import { VersionHistory } from "./VersionHistory/VersionHistory";

type SubmissionsBuilderProps = {
  existingSubmissionType?: GetSubmissionTypeForBuilderQuery["submissionType"];
};

function SubmissionsBuilderInner({
  existingSubmissionType,
}: SubmissionsBuilderProps) {
  const { account } = useContext(AuthContext);
  const { existingSubmissionType: contextSubmissionType, selectedVersionId } =
    useSubmissionsBuilderContext();
  const defaultValues = useMemo(
    () => getDefaultValues(existingSubmissionType, selectedVersionId),
    [existingSubmissionType, selectedVersionId],
  );

  const builderFormMethods = useBuilderForm({
    mode: "onChange",
    defaultValues,
    resolver: customResolver,
  });

  useEffect(() => {
    builderFormMethods.reset(defaultValues);
  }, [defaultValues, builderFormMethods]);

  const { setValue, getValues } = builderFormMethods;

  useShowCreateSubmissionTypeModalOnEnter({
    existingSubmissionType,
    setValue,
    getValues,
  });

  return (
    <FormProvider {...builderFormMethods}>
      <SubmissionsBuilderContent
        account={account}
        existingSubmissionType={contextSubmissionType}
      />
    </FormProvider>
  );
}

function SubmissionsBuilderContent({
  account,
  existingSubmissionType,
}: {
  account: AuthContextType["account"];
  existingSubmissionType: GetSubmissionTypeForBuilderQuery["submissionType"];
}) {
  const [mode] = useSubmissionsBuilderMode();
  const isVersionHistoryMode =
    mode === SUBMISSIONS_BUILDER_MODE.VERSION_HISTORY;

  const { ChatToggleButton, ChatPanel, isChatPanelOpen } =
    useSubmissionBuilderV2ChatAgent({
      account,
      existingSubmissionType,
    });

  return (
    <FormLayout
      chatToggleButton={ChatToggleButton}
      chatPanel={ChatPanel}
      isChatPanelOpen={isChatPanelOpen}
    >
      <SubmissionsBuilderWrapper hasRightSidebar={isVersionHistoryMode}>
        <SidebarColumn />
        <MainColumn />
        {isVersionHistoryMode && <VersionHistory />}
      </SubmissionsBuilderWrapper>
    </FormLayout>
  );
}

function FormLayout({
  children,
  chatToggleButton,
  chatPanel,
  isChatPanelOpen,
}: PropsWithChildren<{
  chatToggleButton: React.ReactNode;
  chatPanel: React.ReactNode;
  isChatPanelOpen: boolean;
}>) {
  const location = useLocation<undefined | { prevLocation?: string }>();
  const prevLocation =
    location.state?.prevLocation ?? "/settings/account/records";

  const formName = useBuilderWatch("name");
  const subtitle = formName ? `${formName} form` : "Add record type";

  return (
    <FullPageFormLayout
      subtitle={subtitle}
      prevLocation={prevLocation}
      middleContainer={<HeaderMiddleContainer />}
      rightContainer={<MenuButtons chatToggleButton={chatToggleButton} />}
      centered={false}
      width="auto"
      isSidePanelOpen={isChatPanelOpen}
      sidePanel={chatPanel as JSX.Element}
    >
      {children}
    </FullPageFormLayout>
  );
}

export function SubmissionsBuilderV2() {
  const { submissionTypeId } = useParams<{ submissionTypeId?: string }>();

  const { data, error, loading } = useGetSubmissionTypeForBuilderQuery({
    fetchPolicy: "cache-and-network",
    variables: { submissionTypeId: submissionTypeId! },
    skip: !submissionTypeId,
  });

  if (error) {
    return <em>There was a problem loading your submission</em>;
  }

  if (loading) {
    return <FullPageLoadingScreen />;
  }

  return (
    <SubmissionsBuilderContextProvider
      existingSubmissionType={data?.submissionType}
    >
      <SubmissionsBuilderInner existingSubmissionType={data?.submissionType} />
    </SubmissionsBuilderContextProvider>
  );
}
